import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, ChevronDown, Check } from "lucide-react";
import { NAV } from "@/data";
import { LANGS, useLang } from "@/i18n";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

function LanguageSwitcher({ compact }) {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const current = LANGS.find((l) => l.code === lang);

  return (
    <div className="relative">
      <button
        data-testid="language-switcher"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 font-satoshi font-bold text-sm uppercase tracking-wide px-4 py-2 border border-[#27272A] hover:border-brand transition-colors duration-200 ${compact ? "w-full justify-between" : ""}`}
      >
        <Globe size={15} />
        {current?.label}
        <ChevronDown size={15} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className={`absolute right-0 mt-2 min-w-[180px] bg-[#09090B] border border-[#27272A] z-50 ${compact ? "left-0" : ""}`}
            data-testid="language-menu"
          >
            {LANGS.map((l) => (
              <li key={l.code}>
                <button
                  data-testid={`language-option-${l.code}`}
                  onClick={() => { setLang(l.code); setOpen(false); }}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 font-satoshi font-bold text-sm text-left hover:bg-brand hover:text-white transition-colors duration-150 ${l.code === lang ? "text-accent" : "text-[#fafafa]"}`}
                >
                  {l.name}
                  {l.code === lang && <Check size={15} />}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 border-b ${
        scrolled ? "bg-[#09090B]/80 backdrop-blur-xl border-[#27272A]" : "border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("#top")}
          data-testid="logo-button"
          className="font-cabinet font-black uppercase tracking-tight text-lg md:text-xl flex items-center gap-2"
        >
          <span className="w-3 h-3 bg-brand inline-block" />
          Jaxson Dinmore
        </button>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((item) => (
            <button
              key={item.href}
              data-testid={`nav-${item.key}`}
              onClick={() => scrollTo(item.href)}
              className="font-satoshi font-bold text-sm uppercase tracking-wide px-4 py-2 hover:bg-brand hover:text-white transition-colors duration-200"
            >
              {t.nav[item.key]}
            </button>
          ))}
          <button
            onClick={() => scrollTo("#contact")}
            data-testid="header-cta"
            className="mx-2 font-satoshi font-bold text-sm uppercase tracking-wide px-5 py-3 bg-brand text-white hover:bg-white hover:text-[#09090B] transition-colors duration-200"
          >
            {t.nav.letstalk}
          </button>
          <LanguageSwitcher />
        </nav>

        <button
          className="md:hidden font-satoshi font-bold text-sm uppercase px-4 py-2 border border-[#27272A]"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Close" : "Menu"}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#09090B] border-b border-[#27272A]"
          >
            <div className="flex flex-col">
              {NAV.map((item) => (
                <button
                  key={item.href}
                  data-testid={`mobile-nav-${item.key}`}
                  onClick={() => { scrollTo(item.href); setOpen(false); }}
                  className="font-cabinet font-bold uppercase text-3xl text-left px-6 py-4 border-t border-[#27272A] hover:bg-brand hover:text-white transition-colors"
                >
                  {t.nav[item.key]}
                </button>
              ))}
              <button
                data-testid="mobile-nav-contact-cta"
                onClick={() => { scrollTo("#contact"); setOpen(false); }}
                className="font-cabinet font-bold uppercase text-3xl text-left px-6 py-4 border-t border-[#27272A] bg-brand text-white"
              >
                {t.nav.letstalk}
              </button>
              <div className="px-6 py-4 border-t border-[#27272A]">
                <LanguageSwitcher compact />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
